import { FC } from 'react';

import { SidebarIconBase, SidebarIconProps } from './SidebarIcon';

/**
 * Ikony line-art dla PODPOZYCJI menu (flyout + rozwinięty accordion). Ten sam
 * język wizualny i mechanika animacji co ikony top-level z `icons.tsx`: element-
 * sygnatura oznaczony klasą `sb-*`, a na hover wiersza menu coś „dzieje się" na
 * ikonie (rysowanie, obrót, dzwonienie, fala, mrugnięcie) — bez skalowania całości.
 */

const dot = { fill: 'currentColor', stroke: 'none' } as const;

// Informacje hotelowe — „i" w okręgu (kropka pulsuje).
export const InfoIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={12} cy={12} r={8.5} />
    <circle className="sb-pop" cx={12} cy={8} r={1.15} {...dot} />
    <path d="M12 11.5 V16.5" />
  </SidebarIconBase>
);

// Udogodnienia — iskry/sparkles (udogodnienia „premium"); migoczą po hover.
export const AmenitiesIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path
      className="sb-pop"
      d="M10.5 4 C10.9 7 12 8.1 15 8.5 C12 8.9 10.9 10 10.5 13 C10.1 10 9 8.9 6 8.5 C9 8.1 10.1 7 10.5 4 Z"
      {...dot}
    />
    <path
      className="sb-pop sb-pop-2"
      d="M16.5 13 C16.7 14.7 17.3 15.3 19 15.5 C17.3 15.7 16.7 16.3 16.5 18 C16.3 16.3 15.7 15.7 14 15.5 C15.7 15.3 16.3 14.7 16.5 13 Z"
      {...dot}
    />
  </SidebarIconBase>
);

// Wizytówki — karta z avatarem; wiersze danych „wpisują się".
export const BusinessCardIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={3} y={6} width={18} height={12} rx={2} />
    <circle cx={8} cy={11} r={2.2} />
    <path className="sb-draw" d="M13 10 H18 M13 13 H18 M6 15.5 H10" />
  </SidebarIconBase>
);

// Dobre praktyki — kciuk w górę (kiwa z aprobatą).
export const GoodPracticeIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-swing">
      <path d="M7.5 11 v8 H5.5 a1 1 0 0 1 -1 -1 v-6 a1 1 0 0 1 1 -1 z" />
      <path d="M7.5 11 l3.4 -7 a2 2 0 0 1 2.6 2.2 L12.8 9 H18 a2 2 0 0 1 2 2.4 l-1 5 a2.4 2.4 0 0 1 -2.4 2.1 H7.5" />
    </g>
  </SidebarIconBase>
);

// Książka telefoniczna — książka z kontaktem (kontakt „pojawia się").
export const PhonebookIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={5} y={4} width={14} height={16} rx={2} />
    <path d="M9 4 V20" />
    <circle className="sb-pop" cx={14.5} cy={10} r={1.7} {...dot} />
    <path d="M11.8 15.5 a2.7 2 0 0 1 5.4 0 Z" {...dot} />
  </SidebarIconBase>
);

// Wifi — łuki sygnału rozchodzą się falami.
export const WifiIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={12} cy={18} r={1.4} {...dot} />
    <path className="sb-wave" d="M8.5 14.5 a5 5 0 0 1 7 0" />
    <path className="sb-wave sb-wave-2" d="M6 12 a9 9 0 0 1 12 0" />
    <path className="sb-wave sb-wave-3" d="M3.5 9.5 a13 13 0 0 1 17 0" />
  </SidebarIconBase>
);

// Regulaminy — dokument z zagiętym rogiem; tekst „wpisuje się".
export const RegulationsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M6 3 h8 l4 4 v13 a1 1 0 0 1 -1 1 H7 a1 1 0 0 1 -1 -1 z" />
    <path d="M14 3 v4 h4" />
    <path className="sb-draw" d="M9 11.5 H15 M9 14.5 H15 M9 17.5 H13" />
  </SidebarIconBase>
);

// Poi — pinezka mapy z pulsującym pierścieniem lokalizacji.
export const PoiIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M12 21 s6 -6.5 6 -11 a6 6 0 1 0 -12 0 c0 4.5 6 11 6 11 z" />
    <circle cx={12} cy={10} r={2.2} />
    <ellipse className="sb-wave" cx={12} cy={21} rx={4.5} ry={1.5} fill="none" />
  </SidebarIconBase>
);

// Kanały TV — telewizor z anteną; symbol „play" pulsuje.
export const TvIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={3} y={8} width={18} height={12} rx={2} />
    <path d="M8.5 8 L12 4.5 L15.5 8" />
    <circle cx={12} cy={4} r={1} {...dot} />
    <path className="sb-pop" d="M10.5 11 L15 14 L10.5 17 Z" {...dot} />
  </SidebarIconBase>
);

// Faq — dymek z „?" (przekrzywia się pytająco).
export const FaqIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={4} y={4} width={16} height={13} rx={3} />
    <path d="M9 17 V20.5 L13 17" />
    <g className="sb-swing">
      <path d="M9.7 9 a2.4 2.4 0 0 1 4.6 0.8 c0 1.7 -2.3 1.8 -2.3 3.2" />
      <circle cx={12} cy={14.6} r={1} {...dot} />
    </g>
  </SidebarIconBase>
);

// Program Lojalnościowy — gwiazda (migocze).
export const LoyaltyStarIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path
      className="sb-pop"
      d="M12 3 l2.6 5.6 6 0.8 -4.4 4.2 1.1 6.1 -5.3 -2.9 -5.3 2.9 1.1 -6.1 -4.4 -4.2 6 -0.8 z"
    />
  </SidebarIconBase>
);

// SocialMedia — graf udostępniania; połączenia „rysują się".
export const SocialMediaIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={6} cy={12} r={2.4} />
    <circle cx={17} cy={6} r={2.4} />
    <circle cx={17} cy={18} r={2.4} />
    <path className="sb-draw" d="M8.2 11 L14.8 7 M8.2 13 L14.8 17" />
  </SidebarIconBase>
);

// Wirtualny spacer — kompas (igła się obraca).
export const CompassIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={12} cy={12} r={9} />
    <g className="sb-spin">
      <rect x={3} y={3} width={18} height={18} fill="none" stroke="none" />
      <path d="M12 6.5 L14 12 L12 17.5 L10 12 Z" />
    </g>
    <circle cx={12} cy={12} r={1} {...dot} />
  </SidebarIconBase>
);

// Linki do recenzji — gwiazda + strzałka „w świat" (link rysuje się).
export const ReviewsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M9.5 3 l2.1 4.4 4.8 0.7 -3.5 3.4 0.8 4.8 -4.2 -2.2 -4.2 2.2 0.8 -4.8 -3.5 -3.4 4.8 -0.7 z" />
    <path className="sb-draw" d="M15 16 H20 V21 M20 16 L14.5 21.5" />
  </SidebarIconBase>
);

// Bloki niestandardowe — trzy figury; koło pulsuje.
export const ShapesIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M3 9 L6.5 3 L10 9 Z" />
    <rect x={13} y={4} width={7} height={7} rx={1.5} />
    <circle className="sb-pop" cx={9} cy={16.5} r={4} />
  </SidebarIconBase>
);

// Zamówienia Room Service — talerz z unoszącą się parą.
export const RoomServiceIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path className="sb-wave" d="M10.5 4 q-1.3 1.4 0 2.8 q1.3 1.4 0 2.8" />
    <path className="sb-wave sb-wave-2" d="M13.5 4 q-1.3 1.4 0 2.8 q1.3 1.4 0 2.8" />
    <circle cx={12} cy={15} r={5.5} />
    <circle cx={12} cy={15} r={2} />
  </SidebarIconBase>
);

// Serwis śniadaniowy — jajko sadzone: nieregularne białko + żółtko.
// Świadomie BEZ pary/kopuły — te motywy nosi już `RoomServiceIcon`, a obie
// pozycje stoją obok siebie w grupie „Usługi" i muszą się różnić na pierwszy rzut oka.
export const BreakfastIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M11.4 3.6c2.1-.3 3 1 4.2 1.8 1.5 1 2.9 1.7 3.2 3.5.3 1.8-1 2.8-1.1 4.1-.1 1.5.6 2.9-.6 4.1-1.2 1.3-3 .8-4.4 1.3-1.4.5-2.6 1.8-4.3 1.4-1.8-.4-2.4-2-3.4-3.1-1.1-1.2-2.6-2-2.6-3.8 0-1.8 1.6-2.6 2.3-4 .7-1.4.6-3.1 2.2-3.9 1.5-.8 3-1.1 4.5-1.4Z" />
    <circle cx={10.6} cy={11.4} r={3} />
  </SidebarIconBase>
);

// Zakupy Store — pudełko / paczka (unosi się).
export const StoreBoxIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-float">
      <path d="M12 3 L20 7 V17 L12 21 L4 17 V7 Z" />
      <path d="M4 7 L12 11 L20 7" />
      <path d="M12 11 V21" />
    </g>
  </SidebarIconBase>
);

// Rezerwacje — kalendarz z zegarem (wskazówki się obracają).
export const ReservationsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={3} y={5} width={12} height={11} rx={2} />
    <path d="M3 8.5 H15" />
    <path d="M6.5 3.5 V6 M11.5 3.5 V6" />
    <circle cx={16} cy={16} r={5.5} />
    <g className="sb-spin">
      <rect x={10.5} y={10.5} width={11} height={11} fill="none" stroke="none" />
      <path d="M16 16 V12.5" />
      <path d="M16 16 L18.5 17" />
    </g>
  </SidebarIconBase>
);

// Wszystkie (wydarzenia) — kalendarz; wiersz dni „skacze".
export const AllEventsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={4} y={5} width={16} height={15} rx={2} />
    <path d="M4 9.5 H20" />
    <path d="M9 3 V6.5 M15 3 V6.5" />
    <circle className="sb-bounce" cx={9} cy={14} r={1.2} {...dot} />
    <circle className="sb-bounce sb-bounce-2" cx={12} cy={14} r={1.2} {...dot} />
    <circle className="sb-bounce sb-bounce-3" cx={15} cy={14} r={1.2} {...dot} />
    <circle cx={9} cy={17.5} r={1.2} {...dot} />
    <circle cx={12} cy={17.5} r={1.2} {...dot} />
  </SidebarIconBase>
);

// Utwórz / Dodaj — plus w okręgu (rysuje się).
export const PlusIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={12} cy={12} r={9} />
    <path className="sb-draw" d="M12 8 V16 M8 12 H16" />
  </SidebarIconBase>
);

// Kategorie / Dodaj kategorię — metka (delikatnie się kiwa).
export const TagIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-swing">
      <rect x={2} y={2} width={20} height={20} fill="none" stroke="none" />
      <path d="M3.5 4 H12 L20 12 a1.5 1.5 0 0 1 0 2 L14 20 a1.5 1.5 0 0 1 -2 0 L3.5 11.5 Z" />
      <circle cx={7.5} cy={8} r={1.5} {...dot} />
    </g>
  </SidebarIconBase>
);

// Lista (rezerwacji / ofert) — checklista; ptaszek się rysuje.
export const ListChecksIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path className="sb-draw" d="M3 6.5 L4.5 8 L7 5" />
    <path d="M10 6.5 H20" />
    <circle cx={4} cy={12.5} r={1.1} {...dot} />
    <path d="M10 12.5 H20" />
    <circle cx={4} cy={17.5} r={1.1} {...dot} />
    <path d="M10 17.5 H17" />
  </SidebarIconBase>
);

// Powiadomienia — dzwonek (dzwoni).
export const BellIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-swing">
      <rect x={4} y={2} width={16} height={18} fill="none" stroke="none" />
      <path d="M12 4 a6 6 0 0 0 -6 6 c0 6 -2.5 7 -2.5 7 h17 s-2.5 -1 -2.5 -7 a6 6 0 0 0 -6 -6 z" />
      <path d="M12 2.5 V4" />
    </g>
    <path d="M10 20 a2 2 0 0 0 4 0" />
  </SidebarIconBase>
);

// Kody rabatowe — kupon z „%" (ukośnik się rysuje).
export const PromoIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={3} y={7} width={18} height={10} rx={2} />
    <path d="M14 7 V9 M14 11 V13 M14 15 V17" />
    <circle cx={7.5} cy={11} r={1.1} {...dot} />
    <circle cx={10.5} cy={13} r={1.1} {...dot} />
    <path className="sb-draw" d="M10.7 10.5 L7.3 13.5" />
  </SidebarIconBase>
);

// Kampanie Push — kursor z falą kliknięcia.
export const PushClickIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M6 4 L6 17 L9.5 13.5 L12 19 L14 18 L11.5 12.5 L16 12 Z" />
    <path className="sb-wave" d="M14 5 a4 4 0 0 1 4 4" />
    <path className="sb-wave sb-wave-2" d="M15 3 a7 7 0 0 1 6 6" />
  </SidebarIconBase>
);

// Subskrybenci — dwa awatary; przedni „dołącza".
export const SubscribersIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={15} cy={9} r={2.8} />
    <path d="M10.5 18.5 a4.8 4.2 0 0 1 9.6 0" />
    <g className="sb-pop">
      <circle cx={9} cy={10} r={3.2} />
      <path d="M4 19 a5.2 4.6 0 0 1 10 0" />
    </g>
  </SidebarIconBase>
);

// Spa — kwiat lotosu (środek „rozkwita").
export const SpaFlowerIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M12 5 C10 9 10 13 12 16 C14 13 14 9 12 5 Z" />
    <path d="M12 16 C8 14 6 11 6 8 C9 9 11 12 12 16 Z" />
    <path d="M12 16 C16 14 18 11 18 8 C15 9 13 12 12 16 Z" />
    <path d="M5 18 q7 3 14 0" />
    <circle className="sb-pop" cx={12} cy={15.5} r={1.5} {...dot} />
  </SidebarIconBase>
);

// Produkty i Usługi — torba na zakupy (uchwyt się kiwa).
export const ProductsBagIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M6 8 h12 l0.7 11 a1.6 1.6 0 0 1 -1.6 1.7 H6.9 a1.6 1.6 0 0 1 -1.6 -1.7 z" />
    <g className="sb-swing">
      <rect x={6} y={3} width={12} height={7} fill="none" stroke="none" />
      <path d="M9 8.5 V7 a3 3 0 0 1 6 0 v1.5" />
    </g>
  </SidebarIconBase>
);

// Wypożyczalnia Produktów — wózek sklepowy (podskakuje jak w ruchu).
export const RentalCartIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-bounce">
      <path d="M3 4 H5 L7 15 H18 L20 7 H6" />
      <circle cx={9} cy={19} r={1.6} {...dot} />
      <circle cx={17} cy={19} r={1.6} {...dot} />
    </g>
  </SidebarIconBase>
);
